import Swiper from 'swiper';
import { Navigation, Pagination } from 'swiper/modules';
import type { PrototypeState } from './prototype-state';

const SEL_PANEL = '.canvas-3d-configurator-panel';
const SEL_CONFIG_ITEM = '.canvas-config-item';
const SEL_STEP = '.canvas-3d-step';
const SEL_PRODUCT_WRAPPER = '.edition-product-wrapper';
const SEL_SUMMARY_PANEL = '.canvas-summary-content';
const SEL_SUMMARY_STEP = '.summary-step';
/** The product ensureDefaultSelection would pick: the first single on the canvas. */
const FIRST_PRODUCT_ID = '101';
const RRP_FACTOR = 1.65;
const LOADING_PERCENTAGE = 45;

const priceFormat = new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' });

/**
 * Prototype stand-in for `Scripts/pages/canvas-3d-configurator-page.ts`: the same classes and attributes on the same
 * markup, without Product3D, the price worker or the cart. Clicks the live page answers locally (picking a product or an
 * option, opening a step) stay local here too; the ones the panel also owns go through `requestState` so the URL follows.
 */
export class CanvasPage {
    private readonly root: HTMLElement;
    private readonly page: HTMLElement;
    private readonly panel: HTMLElement;
    private readonly requestState: (changed: Partial<PrototypeState>) => void;
    private state: PrototypeState | null = null;
    private readonly swipers = new Map<HTMLElement, Swiper>();
    private popupSwiper: Swiper | null = null;
    private sceneReady = false;

    constructor(root: HTMLElement, requestState: (changed: Partial<PrototypeState>) => void) {
        const page = root.querySelector<HTMLElement>('.canvas-3d-configurator-page');
        const panel = root.querySelector<HTMLElement>(SEL_PANEL);
        if (!page || !panel) {
            throw new Error('Prototype markup is incomplete: no .canvas-3d-configurator-page');
        }

        this.root = root;
        this.page = page;
        this.panel = panel;
        this.requestState = requestState;

        this.bindHeaderHeight();
        this.bindClicks();
        this.bindImagePopup();
        // canvas-scene.mjs answers once its first frame is drawn; until then the viewport is blank, as on live.
        this.sceneReady = this.sceneHost()?.getAttribute('data-scene-ready') === '1';
        this.page.addEventListener('prototype-scene-ready', () => {
            this.sceneReady = true;
            if (this.state) {
                this.apply(this.state);
            }
        });
        this.panel.querySelectorAll<HTMLElement>('.content-with-read-more').forEach(box => CanvasPage.bindReadMore(box));
        this.panel.querySelectorAll<HTMLElement>(`${SEL_STEP} .edition-config`).forEach(control => this.updateStepSummary(control));
        this.renderAddOnPrices();
        this.setConfigurable(this.fallbackConfigurable());
    }

    public apply(state: PrototypeState) {
        const previous = this.state;
        this.state = state;

        const viewOnly = state.viewOnly === 'on';
        this.page.setAttribute('view-only-mode', viewOnly ? '1' : '0');
        this.page.setAttribute('expand', viewOnly || state.sidebar === 'collapsed' ? '0' : '1');

        const progress = this.page.querySelector<HTMLElement>('.circular-progress');
        progress?.classList.toggle('active', state.scene === 'loading' || (state.scene === 'ready' && !this.sceneReady));
        this.setProgress(progress, LOADING_PERCENTAGE);

        // Only on a change of these states: the products and options a reviewer picked by hand survive every other one.
        const changed = (key: keyof PrototypeState) => !previous || previous[key] !== state[key];
        this.panel.querySelector('.step-pick-item')?.classList.toggle('step-hidden', state.products === 'single');
        if (changed('products') && state.products === 'single') {
            this.selectOnly(FIRST_PRODUCT_ID);
        }

        if (changed('cart') && state.cart === 'disabled') {
            this.clearSelection();
        } else if (state.cart !== 'disabled' && !this.panel.querySelector(`${SEL_CONFIG_ITEM}.active`)) {
            this.selectOnly(FIRST_PRODUCT_ID);
        }

        if (changed('steps')) {
            this.panel.querySelectorAll<HTMLElement>(SEL_STEP).forEach(step => step.setAttribute('expand', state.steps === 'open' ? '1' : '0'));
        }

        const loading = state.cart === 'loading';
        const addToCart = this.page.querySelector<HTMLButtonElement>('.canvas-3d-configurator-bt-add-to-cart');
        if (addToCart) {
            addToCart.disabled = loading || state.cart === 'disabled';
            addToCart.setAttribute('data-loading', loading ? '1' : '0');
        }

        this.page.querySelectorAll<HTMLButtonElement>('.canvas-3d-configurator-bt-request-quotation').forEach(button => {
            button.disabled = state.cart === 'disabled';
        });
        const error = state.cart === 'failed' || state.cart === 'canceled' ? state.cart : 'none';
        this.page.querySelector('.canvas-3d-configurator-add-to-cart-error')?.setAttribute('data-error', error);
        this.page.querySelectorAll<HTMLElement>(SEL_PANEL).forEach(panel => panel.toggleAttribute('inert', loading));
        this.page.querySelector('.bottom-content')?.classList.toggle('content-b2b', state.brand === 'de-eekhoorn');

        // ConfigurationOverview: one column when the intro or the accordions are missing.
        this.page.querySelector('.overview-container')?.classList.toggle('single-column', state.overview === 'intro' || state.overview === 'accordions');

        this.renderPrices();
        this.syncAISliders();
        this.page.querySelector(SEL_SUMMARY_PANEL)?.setAttribute('expand', state.panel === 'summary' ? '1' : '0');
        if (state.panel === 'summary') {
            this.renderSummary();
        }
    }

    // == Selection ==============================================================================================

    private bindClicks() {
        this.page.addEventListener('click', (e: MouseEvent) => {
            const target = e.target instanceof Element ? e.target : null;
            if (!target) {
                return;
            }

            if (target.closest('.sidebar-expand-bt')) {
                this.requestState({ sidebar: this.page.getAttribute('expand') === '1' ? 'collapsed' : 'open' });
            } else if (target.closest('.canvas-3d-configurator-bt-view-summary')) {
                this.requestState({ panel: 'summary' });
            } else if (target.closest('.canvas-3d-configurator-bt-hide-summary')) {
                this.requestState({ panel: 'configure' });
            } else if (target.closest('.ai-slider-bt-expand')) {
                this.openImagePopup(target.closest<HTMLElement>('.ai-slider-item'));
            } else if (target.closest('.canvas-3d-bt-view-room')) {
                this.page.querySelector('.canvas-3d-ai-slider:not(.hidden)')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            } else if (target.closest('.canvas-3d-bt-ruler')) {
                // bindRulerButton: a client-side flag on the button, and the scene draws the measurements.
                const on = target.closest('.canvas-3d-bt-ruler')?.classList.toggle('active') ?? false;
                this.sceneHost()?.setAttribute('data-ruler', on ? '1' : '0');
            } else if (target.closest('.canvas-3d-bt-reset')) {
                this.sendSceneCommand('reset');
            } else if (target.closest('.canvas-3d-bt-zoom-out')) {
                this.sendSceneCommand('fit');
            } else {
                this.onConfiguratorClick(e, target);
            }
        });
    }

    private onConfiguratorClick(e: MouseEvent, target: Element) {
        const item = target.closest<HTMLElement>(SEL_CONFIG_ITEM);
        const option = target.closest<HTMLElement>('.edition-config .items .item');
        const toggle = target.closest<HTMLElement>('.add-on-toggle-bt');
        const readMore = target.closest<HTMLElement>('.content-with-read-more .read-more, .content-with-read-more .read-less');
        const stepHeader = target.closest<HTMLElement>(`${SEL_STEP} > .collapsed-bt, ${SEL_SUMMARY_STEP} > .collapsed-bt, .configuration-overview-accordion > .collapsed-bt`);

        if (item) {
            this.toggleSelection(item);
        } else if (option) {
            // Option links (a description that is an absolute URL) navigate on live; here they only select.
            e.preventDefault();
            this.selectOption(option);
        } else if (toggle) {
            this.toggleAddOn(toggle);
        } else if (readMore) {
            readMore.closest('.content-with-read-more')?.setAttribute('expand', readMore.classList.contains('read-more') ? '1' : '0');
        } else if (stepHeader) {
            const step = stepHeader.parentElement;
            step?.setAttribute('expand', step.getAttribute('expand') === '1' ? '0' : '1');
        }
    }

    /** `toggleSelection`: a single replaces the other singles, a multi toggles; the item clicked becomes the one configured. */
    private toggleSelection(item: HTMLElement) {
        if (item.classList.contains('single-pd')) {
            this.items('.single-pd.active').forEach(current => current.classList.toggle('active', current === item));
            item.classList.add('active');
            this.setConfigurable(item);
        } else if (item.classList.contains('active') && !item.classList.contains('selecting')) {
            this.setConfigurable(item);
        } else {
            item.classList.toggle('active');
            this.setConfigurable(item.classList.contains('active') ? item : this.fallbackConfigurable());
        }

        this.markConfigured();
    }

    private selectOnly(productId: string) {
        this.items('').forEach(item => item.classList.toggle('active', item.getAttribute('data-id') === productId));
        this.setConfigurable(this.items(`[data-id="${productId}"]`)[0] ?? null);
    }

    private clearSelection() {
        this.items('.active').forEach(item => item.classList.remove('active'));
        this.setConfigurable(null);
    }

    private fallbackConfigurable(): HTMLElement | null {
        return this.items('.single-pd.active')[0] ?? this.items('.active')[0] ?? null;
    }

    /** `setConfigurableItem` + `updateProductItemStates`: `selecting` marks the configured item, and its edition wrapper is the one shown. */
    private setConfigurable(item: HTMLElement | null) {
        this.items('').forEach(current => {
            current.classList.toggle('selecting', current === item);
            const state = current === item ? 'configuring' : current.classList.contains('active') ? 'added' : 'not-added';
            current.setAttribute('data-state', state);
        });

        const productId = item?.getAttribute('data-id') ?? '';
        this.panel.querySelectorAll<HTMLElement>(SEL_PRODUCT_WRAPPER).forEach(wrapper => {
            wrapper.classList.toggle('active', wrapper.getAttribute('data-product-id') === productId);
        });
        this.syncRender();
        this.renderPrices();
        this.syncAISliders();
    }

    /** The config-3d controls are single-select: the option clicked replaces the active one in its edition. */
    private selectOption(option: HTMLElement) {
        const control = option.closest<HTMLElement>('.edition-config');
        control?.querySelectorAll<HTMLElement>('.items .item').forEach(current => current.classList.toggle('active', current === option));
        this.updateStepSummary(option);
        this.syncRender();
        this.markConfigured();
    }

    /** EditionConfigAddOnService: plus adds the last option (the service), minus goes back to the first (without). */
    private toggleAddOn(toggle: HTMLElement) {
        const control = toggle.closest<HTMLElement>('.edition-config');
        const options = Array.from(control?.querySelectorAll<HTMLElement>('.items .item') ?? []);
        const adding = toggle.getAttribute('data-state') === 'plus';
        const chosen = adding ? options[options.length - 1] : options[0];
        options.forEach(current => current.classList.toggle('active', current === chosen));
        toggle.setAttribute('data-state', adding ? 'minus' : 'plus');
        this.updateStepSummary(toggle);
        this.markConfigured();
    }

    /** The live subtitle switches from the from price to the configured total on the shopper's first change. */
    private markConfigured() {
        if (this.state?.price === 'from') {
            this.requestState({ price: 'configured' });
        } else {
            this.renderPrices();
        }
    }

    private updateStepSummary(element: Element) {
        const step = element.closest<HTMLElement>(SEL_STEP);
        const summary = step?.querySelector<HTMLElement>('.selected-item');
        if (step && summary) {
            summary.textContent = Array.from(step.querySelectorAll<HTMLElement>('.item.active'))
                .map(item => item.getAttribute('data-name') ?? '')
                .filter(name => name !== '')
                .join(', ');
        }
    }

    private items(filter: string): Array<HTMLElement> {
        return Array.from(this.panel.querySelectorAll<HTMLElement>(`${SEL_CONFIG_ITEM}${filter}`));
    }

    // == Prices =================================================================================================

    private renderPrices() {
        const state = this.state;
        if (!state) {
            return;
        }

        const unknown = state.price === 'unknown';
        const selected = this.items('.active');
        const fromTotal = selected.reduce((sum, item) => sum + CanvasPage.number(item.getAttribute('data-price')), 0);
        const configuredTotal = selected.reduce((sum, item) => sum + this.configuredPrice(item), 0);

        this.items('').forEach(item => {
            const price = item.querySelector<HTMLElement>('.config-item-price');
            if (price) {
                price.textContent = unknown && item.classList.contains('active') ? this.unknownLabel() : CanvasPage.priceText(this.configuredPrice(item));
            }
        });

        // setPanelSubtitle: an unknown configured total falls back to the from price, never a label around nothing.
        const useFromPrice = state.price !== 'configured';
        const subtitlePrice = useFromPrice ? CanvasPage.priceText(fromTotal) : CanvasPage.priceText(configuredTotal);
        this.page.querySelectorAll<HTMLElement>('.canvas-3d-configurator-sidebar-subtitle').forEach(subtitle => {
            const label = subtitle.getAttribute(useFromPrice ? 'data-from-price-label' : 'data-price-label') ?? '';
            subtitle.textContent = subtitlePrice ? CanvasPage.fillOrAppend(label, subtitlePrice) : '';
        });

        const totalText = unknown ? this.unknownLabel() : CanvasPage.priceText(configuredTotal);
        this.page.querySelectorAll<HTMLElement>('.canvas-3d-configurator-total-price').forEach(total => {
            total.textContent = totalText;
        });

        // The retail column hides as a whole when its amount is unknown: never a caption over a blank amount.
        const rrp = this.page.querySelector<HTMLElement>('.canvas-3d-configurator-rrp-total');
        const rrpText = unknown ? '' : CanvasPage.priceText(Math.round(configuredTotal * RRP_FACTOR));
        if (rrp) {
            rrp.textContent = rrpText;
            rrp.closest('.price-col')?.classList.toggle('hidden', rrpText === '');
        }
    }

    /** The product's own price plus the price of every active option in its edition wrapper. */
    private configuredPrice(item: HTMLElement): number {
        const wrapper = this.panel.querySelector(`${SEL_PRODUCT_WRAPPER}[data-product-id="${item.getAttribute('data-id') ?? ''}"]`);
        const options = Array.from(wrapper?.querySelectorAll<HTMLElement>('.item.active') ?? []);
        return options.reduce((sum, option) => sum + CanvasPage.number(option.getAttribute('data-price')), CanvasPage.number(item.getAttribute('data-price')));
    }

    private renderAddOnPrices() {
        this.panel.querySelectorAll<HTMLElement>('.edition-config-add-on-service').forEach(control => {
            const options = control.querySelectorAll<HTMLElement>('.items .item');
            const add = options[options.length - 1];
            const price = control.querySelector<HTMLElement>('.add-on-service-price');
            if (add && price) {
                price.textContent = CanvasPage.priceText(CanvasPage.number(add.getAttribute('data-price')));
            }
        });
    }

    private unknownLabel(): string {
        return this.page.getAttribute('data-price-unknown-label') ?? 'TK';
    }

    /**
     * `getPriceText`: nothing for a zero or negative amount, so a slot that hides stays empty. LocalizedMoneyHelper
     * rejoins symbol and amount with a plain space where Intl puts a no-break space, so match its output.
     */
    private static priceText(price: number): string {
        return price > 0 ? priceFormat.format(price).replace(/\u00a0/g, ' ') : '';
    }

    /** TranslationPlaceholderHelper.fillOrAppend: `{price}` says where the amount goes; copy without one gets it appended. */
    private static fillOrAppend(label: string, price: string): string {
        return label.indexOf('{price}') >= 0 ? label.replace('{price}', price) : `${label} ${price}`.trim();
    }

    private static number(value: string | null): number {
        const parsed = parseFloat(value ?? '');
        return isNaN(parsed) ? 0 : parsed;
    }

    // == Summary ================================================================================================

    /** renderSummary: a product header only with more than one product, then one row per orderflow step. */
    private renderSummary() {
        const wrapper = this.page.querySelector<HTMLElement>('.canvas-3d-configurator-summary-wrapper');
        if (!wrapper) {
            return;
        }

        const selected = this.items('.active');
        const rows: Array<HTMLElement> = [];
        for (const item of selected) {
            if (selected.length > 1) {
                const header = CanvasPage.element('div', 'summary-product-header', '');
                header.append(
                    CanvasPage.element('span', 'txt-body-l txt-bold summary-product-title', item.getAttribute('data-name') ?? ''),
                    CanvasPage.element('span', 'txt-body-l summary-product-price', this.state?.price === 'unknown' ? this.unknownLabel() : CanvasPage.priceText(this.configuredPrice(item))));
                rows.push(header);
            }

            const editions = this.panel.querySelector(`${SEL_PRODUCT_WRAPPER}[data-product-id="${item.getAttribute('data-id') ?? ''}"]`);
            editions?.querySelectorAll<HTMLElement>(`${SEL_STEP}[data-show-in-orderflow="true"]`).forEach(step => {
                const row = this.summaryStep(step);
                if (row) {
                    rows.push(row);
                }
            });
        }

        wrapper.replaceChildren(...rows);
    }

    private summaryStep(step: HTMLElement): HTMLElement | null {
        const editions = Array.from(step.querySelectorAll<HTMLElement>('.edition-config'))
            .map(control => this.summaryEdition(control))
            .filter((edition): edition is HTMLElement => edition !== null);
        if (editions.length === 0) {
            return null;
        }

        const row = CanvasPage.element('div', 'collapsed-content summary-step', '');
        row.setAttribute('expand', '1');
        const header = CanvasPage.element('button', 'collapsed-bt', '');
        header.setAttribute('type', 'button');
        const title = CanvasPage.element('div', 'text-wrapper', '');
        title.appendChild(CanvasPage.element('span', 'txt-body-l txt-bold summary-title', step.querySelector<HTMLElement>('.canvas-3d-step-title')?.innerText.trim() ?? ''));
        const icon = CanvasPage.element('div', 'detail-wrapper', '');
        const lines = CanvasPage.element('div', 'collapsed-icon', '');
        lines.append(CanvasPage.element('span', 'line', ''), CanvasPage.element('span', 'line', ''));
        icon.appendChild(lines);
        header.append(title, icon);

        const content = CanvasPage.element('div', 'expanded-content canvas-3d-step-content', '');
        const inner = CanvasPage.element('div', 'content-inner', '');
        const detail = CanvasPage.element('div', 'summary-step-detail', '');
        const selected = CanvasPage.element('div', 'txt summary-step-selected', '');
        selected.append(...editions);
        detail.appendChild(selected);
        inner.appendChild(detail);
        content.appendChild(inner);
        row.append(header, content);
        return row;
    }

    /** One edition's values: a "color" edition shows the option image, a "dimension" one gets its unit as a separate cell. */
    private summaryEdition(control: HTMLElement): HTMLElement | null {
        const tags = (control.getAttribute('data-tags-name') ?? '').split(',');
        const values = Array.from(control.querySelectorAll<HTMLElement>('.items .item.active'))
            .map(item => item.getAttribute('data-name') ?? '')
            .filter(name => name !== '');
        if (values.length === 0) {
            return null;
        }

        const edition = CanvasPage.element('div', 'summary-step-selected-edition', '');
        edition.appendChild(CanvasPage.element('span', 'edition-name txt-body-m txt-bold', control.getAttribute('data-name') ?? ''));
        control.querySelectorAll<HTMLElement>('.items .item.active').forEach(item => {
            const text = item.getAttribute('data-name') ?? '';
            const image = item.querySelector<HTMLImageElement>('.image img');
            if (tags.indexOf('color') >= 0 && image) {
                const value = CanvasPage.element('span', 'value-item value-item-image', '');
                const img = document.createElement('img');
                img.src = image.src;
                img.alt = text;
                value.append(img, CanvasPage.element('span', 'txt-body-m', text));
                edition.appendChild(value);
            } else if (tags.indexOf('dimension') >= 0) {
                const value = CanvasPage.element('span', 'value-item value-item-with-unit', '');
                value.append(CanvasPage.element('span', 'txt-body-m value-txt', text), CanvasPage.element('span', 'value-unit txt-body-m', 'cm'));
                edition.appendChild(value);
            } else {
                edition.appendChild(CanvasPage.element('span', 'value-item txt-body-m', text));
            }
        });
        return edition;
    }

    private static element(tag: string, className: string, text: string): HTMLElement {
        const element = document.createElement(tag);
        element.className = className;
        element.textContent = text;
        return element;
    }

    // == AI slider ==============================================================================================

    /**
     * updateAISliders: only the active single's slider shows, and the view-room button exists only while one does.
     * The Swiper is created on first show (AISlider.updateSwiper), since one built on a hidden element measures 0.
     */
    private syncAISliders() {
        const productId = this.items('.single-pd.active')[0]?.getAttribute('data-id') ?? '';
        const enabled = this.state?.aiSlider !== 'hidden';
        let anyShown = false;
        this.page.querySelectorAll<HTMLElement>('.canvas-3d-ai-slider').forEach(slider => {
            const visible = enabled && slider.getAttribute('data-product-id') === productId;
            slider.classList.toggle('hidden', !visible);
            anyShown = anyShown || visible;
            if (visible) {
                this.showSwiper(slider);
            }
        });
        this.page.querySelector('.canvas-3d-bt-view-room')?.classList.toggle('hidden', !anyShown);
    }

    private showSwiper(slider: HTMLElement) {
        const existing = this.swipers.get(slider);
        if (existing) {
            existing.update();
            return;
        }

        const element = slider.querySelector<HTMLElement>('.ai-slider-swiper');
        if (!element) {
            return;
        }

        this.swipers.set(slider, new Swiper(element, {
            modules: [Navigation],
            slidesPerView: 1.15,
            spaceBetween: 6,
            grabCursor: true,
            navigation: {
                prevEl: slider.querySelector<HTMLElement>('.bt-prev'),
                nextEl: slider.querySelector<HTMLElement>('.bt-next'),
            },
            breakpoints: {
                744: { slidesPerView: 1.8 },
                1201: { slidesPerView: 2.4 },
            },
        }));
    }

    // == Image popup ============================================================================================

    /** PopupAISliderImages.open: the slider's visible angles, starting at the one clicked. */
    private openImagePopup(item: HTMLElement | null) {
        const popup = this.imagePopup();
        const slider = item?.closest<HTMLElement>('.canvas-3d-ai-slider');
        if (!popup || !item || !slider) {
            return;
        }

        const angles = Array.from(slider.querySelectorAll<HTMLElement>('.ai-slider-item:not(.hidden):not([hidden])'));
        const slides = angles.map(angle => {
            const slide = CanvasPage.element('div', 'swiper-slide', '');
            const img = document.createElement('img');
            img.src = angle.querySelector('img')?.getAttribute('src') ?? '';
            img.alt = angle.querySelector('img')?.getAttribute('alt') ?? '';
            img.addEventListener('load', () => this.placePopupDisclaimer(popup, this.popupSwiper));
            slide.appendChild(img);
            // A render whose line the panel hid keeps it hidden here too (Styles/_prototype-render-editor.scss).
            slide.toggleAttribute('data-ai-disclaimer-hidden', angle.querySelector<HTMLElement>('.ai-slider-label')?.hidden ?? false);
            return slide;
        });

        this.popupSwiper?.destroy();
        this.popupSwiper = null;
        popup.querySelector('.swiper-wrapper')?.replaceChildren(...slides);
        popup.querySelector('.media')?.classList.toggle('has-multiple', slides.length > 1);

        // Shown before Swiper is built, so it measures the popup rather than a hidden element.
        popup.classList.add('active');
        document.body.classList.add('disable-scroll');

        const swiper = popup.querySelector<HTMLElement>('.image-slider');
        if (swiper) {
            this.popupSwiper = new Swiper(swiper, {
                modules: [Pagination, Navigation],
                slidesPerView: 1,
                initialSlide: Math.max(angles.indexOf(item), 0),
                grabCursor: true,
                navigation: {
                    nextEl: popup.querySelector<HTMLElement>('.bt-next'),
                    prevEl: popup.querySelector<HTMLElement>('.bt-prev'),
                },
                pagination: {
                    el: popup.querySelector<HTMLElement>('.paging'),
                    type: 'bullets',
                    clickable: true,
                },
                on: {
                    init: s => this.placePopupDisclaimer(popup, s),
                    slideChange: s => this.placePopupDisclaimer(popup, s),
                    resize: s => this.placePopupDisclaimer(popup, s),
                },
            });
        }
    }

    /**
     * New: the disclaimer sits at the bottom middle of the active image. object-fit: contain letterboxes the image inside its
     * slide, so CSS alone only knows the slide's edges; this hands the panel where the image itself ends and how wide it is.
     */
    private placePopupDisclaimer(popup: HTMLElement, swiper: Swiper | null) {
        const img = swiper?.slides[swiper.activeIndex]?.querySelector('img');
        const panel = popup.querySelector<HTMLElement>('.panel');
        if (!img || !panel) {
            return;
        }

        const box = img.getBoundingClientRect();
        const scale = img.naturalWidth > 0 && img.naturalHeight > 0 ? Math.min(box.width / img.naturalWidth, box.height / img.naturalHeight) : 0;
        const width = scale > 0 ? img.naturalWidth * scale : box.width;
        const height = scale > 0 ? img.naturalHeight * scale : box.height;
        const imageBottom = box.bottom - (box.height - height) / 2;
        panel.style.setProperty('--popup-image-bottom', `${Math.round(panel.getBoundingClientRect().bottom - imageBottom)}px`);
        panel.style.setProperty('--popup-image-width', `${Math.round(width)}px`);
    }

    /** ImageSliderPopupBase: the close button, the backdrop and Escape close it. */
    private bindImagePopup() {
        const popup = this.imagePopup();
        if (!popup) {
            return;
        }

        const close = () => {
            popup.classList.remove('active');
            document.body.classList.remove('disable-scroll');
        };
        popup.querySelector('.bt-close')?.addEventListener('click', close);
        popup.querySelector('.bg')?.addEventListener('click', close);
        document.addEventListener('keydown', (e: KeyboardEvent) => {
            if (e.key === 'Escape' && popup.classList.contains('active')) {
                close();
            }
        });
    }

    private imagePopup(): HTMLElement | null {
        return this.root.querySelector<HTMLElement>('.popup-ai-slider-images');
    }

    // == Prototype-only stand-ins ===============================================================================

    /**
     * What the scene iframe is told on live (per-object messages): which products are on the canvas and how each is
     * configured. Here it lands on `.canvas-3d-scene` as attributes Scripts/canvas-scene.mjs renders from.
     */
    private syncRender() {
        const host = this.sceneHost();
        if (!host) {
            return;
        }

        const single = this.items('.single-pd.active')[0] ?? null;
        const shelves = this.items('.multi-pd.active')[0] ?? null;
        host.setAttribute('data-product', single?.getAttribute('data-gltf') ?? '');
        host.setAttribute('data-model', this.activeOptionKey(single, 'Model') ?? 'dressoir');
        host.setAttribute('data-color', this.activeOptionKey(single, 'Kleur') ?? 'bordeauxrood');
        host.setAttribute('data-shelves', shelves ? this.activeOptionKey(shelves, 'Kleur') ?? 'zand' : '');
        this.syncSliderImages(single);
    }

    /** The data-key of the active option in a product's edition with that data-event. */
    private activeOptionKey(item: HTMLElement | null, eventName: string): string | null {
        const wrapper = item ? this.panel.querySelector(`${SEL_PRODUCT_WRAPPER}[data-product-id="${item.getAttribute('data-id') ?? ''}"]`) : null;
        return wrapper?.querySelector(`.edition-config[data-event="${eventName}"] .items .item.active`)?.getAttribute('data-key') ?? null;
    }

    /** AISlider.refresh points each angle at the render of the configuration; here, the real photo in the chosen colour. */
    private syncSliderImages(single: HTMLElement | null) {
        const color = this.activeOptionKey(single, 'Kleur') ?? 'bordeauxrood';
        this.page.querySelectorAll<HTMLElement>(`.canvas-3d-ai-slider[data-product-id="${single?.getAttribute('data-id') ?? ''}"] .ai-slider-item`).forEach(slide => {
            const renders = JSON.parse(slide.getAttribute('data-prototype-renders') ?? '{}') as Record<string, string>;
            const src = renders[color] ?? renders['bordeauxrood'];
            if (src) {
                slide.querySelector('source')?.setAttribute('srcset', src);
                slide.querySelector('img')?.setAttribute('src', src);
            }
        });
    }

    private sceneHost(): HTMLElement | null {
        return this.page.querySelector<HTMLElement>('.canvas-3d-scene');
    }

    private sendSceneCommand(command: 'reset' | 'fit') {
        this.sceneHost()?.dispatchEvent(new CustomEvent('prototype-scene-command', { detail: command }));
    }

    private setProgress(progress: HTMLElement | null, percentage: number) {
        const ring = progress?.querySelector<HTMLElement>('.progress');
        const label = progress?.querySelector<HTMLElement>('.percentage');
        if (ring && label) {
            ring.style.backgroundImage = `conic-gradient(var(--theme-color-1, #138D7F) ${percentage}%, #ffffff 0)`;
            label.textContent = `${percentage}%`;
        }
    }

    /** ContentWithReadMore: the buttons show only when the text outgrows `--line-max` lines, and it then starts collapsed. */
    private static bindReadMore(box: HTMLElement) {
        const content = box.querySelector<HTMLElement>('.content-with-read-more-wrapper');
        const buttons = box.querySelector<HTMLElement>('.about-desc');
        let collapsed = false;
        new ResizeObserver(() => {
            if (!content || content.scrollHeight === 0) {
                return;
            }

            const style = window.getComputedStyle(box);
            const limit = parseInt(style.getPropertyValue('--line-height'), 10) * parseInt(style.getPropertyValue('--line-max'), 10);
            const overflows = content.scrollHeight > limit;
            buttons?.classList.toggle('hidden', !overflows);
            if (overflows && !collapsed) {
                collapsed = true;
                box.setAttribute('expand', '0');
            }
        }).observe(content ?? box);
    }

    /** Header.setHeaderHeightVariable: the theme's header script sets --header-height on <main>, which the page grid needs. */
    private bindHeaderHeight() {
        const header = this.root.querySelector<HTMLElement>('.header');
        const update = () => this.page.style.setProperty('--header-height', `${header?.clientHeight ?? 0}px`);
        update();
        window.addEventListener('resize', update, { passive: true });
    }
}
